import { irisEnabled, loadSession } from '../../auth/iris';

type PendingRequest = {
  reject: (reason?: unknown) => void;
  resolve: (value: BrokerConsoleResponse) => void;
  timeoutId: number;
};

type BrokerConsoleResponse = {
  code?: number;
  command?: string;
  error?: {
    message?: string;
    type?: string;
    [key: string]: unknown;
  };
  gateway_id?: string;
  id?: string;
  ok?: boolean;
  response?: string;
  timeout_ms?: number;
  uuid?: string;
  [key: string]: unknown;
};

type GatewayConsoleClientOptions = {
  accessToken: string;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Event) => void;
  onMessage?: (payload: BrokerConsoleResponse | string) => void;
  onOpen?: () => void;
  wsConsoleUrl: string;
  wsTokenUrl: string;
};

function createRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

function parseSocketMessage(data: string) {
  try {
    return JSON.parse(data) as BrokerConsoleResponse;
  } catch {
    return data;
  }
}

export type { BrokerConsoleResponse, GatewayConsoleClientOptions };

export default class GatewayConsoleClient {
  private accessToken: string;

  private onClose?: (event: CloseEvent) => void;

  private onError?: (error: Event) => void;

  private onMessage?: (payload: BrokerConsoleResponse | string) => void;

  private onOpen?: () => void;

  private pendingRequests = new Map<string, PendingRequest>();

  private socket: WebSocket | null = null;

  private wsConsoleUrl: string;

  private wsTokenUrl: string;

  constructor(options: GatewayConsoleClientOptions) {
    this.accessToken = options.accessToken;
    this.onClose = options.onClose;
    this.onError = options.onError;
    this.onMessage = options.onMessage;
    this.onOpen = options.onOpen;
    this.wsConsoleUrl = options.wsConsoleUrl;
    this.wsTokenUrl = options.wsTokenUrl;
  }

  async connect() {
    if (irisEnabled) await loadSession(true);
    const wsToken = irisEnabled ? '' : await this.fetchWsToken();
    await this.openSocket(wsToken);
  }

  disconnect(reason = 'Closed by client') {
    if (this.socket) {
      this.socket.close(1000, reason);
      this.socket = null;
    }
  }

  isConnected() {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  async sendCommand({
    command,
    gatewayId,
    timeoutMs = 10000
  }: {
    command: string;
    gatewayId: string;
    timeoutMs?: number;
  }) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('The websocket is not connected.');
    }

    const id = createRequestId();
    const payload = {
      id,
      gateway_id: gatewayId,
      command,
      ...(timeoutMs > 0 ? { timeout_ms: timeoutMs } : {})
    };

    return new Promise<BrokerConsoleResponse>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Response timeout exceeded for command "${command}".`));
      }, timeoutMs + 1500);

      this.pendingRequests.set(id, {
        resolve,
        reject,
        timeoutId
      });

      this.socket?.send(JSON.stringify(payload));
    });
  }

  private async fetchWsToken() {
    const response = await fetch(this.wsTokenUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.accessToken}`
      }
    });

    const payload = (await response.json().catch(() => ({}))) as { token?: string; [key: string]: unknown };

    if (!response.ok) {
      throw new Error(`Failed to fetch ws-token (${response.status}).`);
    }

    if (!payload.token) {
      throw new Error('The /ws/token response did not include the token field.');
    }

    return payload.token;
  }

  private async openSocket(wsToken: string) {
    await new Promise<void>((resolve, reject) => {
      const separator = this.wsConsoleUrl.includes('?') ? '&' : '?';
      let socketUrl = `${this.wsConsoleUrl}${separator}token=${encodeURIComponent(wsToken)}`;
      if (irisEnabled) {
        const endpoint = new URL(this.wsConsoleUrl, window.location.origin);
        const target = new URL(window.location.origin);
        target.protocol = 'wss:';
        target.pathname = endpoint.pathname.startsWith('/api/') ? endpoint.pathname : `/api${endpoint.pathname}`;
        target.search = endpoint.search;
        target.searchParams.delete('token');
        socketUrl = target.toString();
      }
      let connectionSettled = false;

      const socket = new WebSocket(socketUrl);
      this.socket = socket;

      socket.onopen = () => {
        connectionSettled = true;
        this.onOpen?.();
        resolve();
      };

      socket.onmessage = event => {
        const payload = parseSocketMessage(event.data);

        if (typeof payload !== 'string' && payload.id && this.pendingRequests.has(payload.id)) {
          const pendingRequest = this.pendingRequests.get(payload.id);

          if (pendingRequest) {
            window.clearTimeout(pendingRequest.timeoutId);
            this.pendingRequests.delete(payload.id);
            pendingRequest.resolve(payload);
            return;
          }
        }

        this.onMessage?.(payload);
      };

      socket.onerror = error => {
        this.onError?.(error);

        if (!connectionSettled) {
          reject(new Error('Failed to open the websocket connection.'));
        }
      };

      socket.onclose = event => {
        this.rejectAllPending(new Error(event.reason || 'Websocket connection closed.'));
        this.socket = null;
        this.onClose?.(event);

        if (!connectionSettled) {
          reject(new Error(`Websocket closed during connection (${event.code}).`));
        }
      };
    });
  }

  private rejectAllPending(error: Error) {
    this.pendingRequests.forEach(pendingRequest => {
      window.clearTimeout(pendingRequest.timeoutId);
      pendingRequest.reject(error);
    });
    this.pendingRequests.clear();
  }
}
